import "./OccupationSelect.scss";

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import Icon from "../../../components/Icon/Icon";
import InputMultiSelect from "../../../components/InputMultiSelect/InputMultiSelect";
import InputText from "../../../components/InputText/InputText";
import CleanModal from "../../../components/modal/CleanModal";
import { trackEvent } from "../../../hooks/useMixpanel";
import useFormData from "../hooks/useFormData";

function socCodesToOptions(socCodes) {
	return socCodes.map((item) => ({
		label: item.soc_name,
		value: item.soc_code,
	}));
}

function selectedSocCodesToLocalValue(socCodes, options) {
	const localValue = socCodes.reduce((acc, val) => {
		const found = options.find((option) => option.value === val);
		if (found) acc.push(found);
		return acc;
	}, []);
	return localValue.length > 0 ? localValue : options;
}

function OccupationsSelect({ onValueChange, includesIcon }) {
	const { currentDashboard } = useSelector((state) => state.scoreInsightsDashboard);
	const earnings = useSelector((state) => state.socEarnings.earnings);
	const { responseFormData, setFormData, formData } = useFormData();
	const [originalSocQuantity, setOriginalSocQuantity] = useState([]);
	const [options, setOptions] = useState([]);
	const [quantityValidationError, setQuantityValidationError] = useState("");
	const [socQuantity, setSocQuantity] = useState([]);
	const [localValue, setLocalValue] = useState([]);
	const [openModal, setOpenModal] = useState(false);

	const getDefaultTargetWage = useCallback(
		(socCode) => {
			if (!earnings || earnings.length === 0) {
				return "0";
			}

			const earningsData = earnings.find((earning) => earning.Occupation === socCode);
			return earningsData ? earningsData["Average Earnings"] : "1";
		},
		[earnings],
	);

	useEffect(() => {
		const options = socCodesToOptions(currentDashboard?.soc ?? []);
		setOptions(options);

		const quantities = formData?.soc_occupation_quantities ?? currentDashboard?.soc_occupation_quantities ?? [];
		setSocQuantity(quantities);
	}, [currentDashboard?._id, formData?.soc_occupation_quantities]);

	useEffect(() => {
		if (!responseFormData?.occupations) return;
		const selected = selectedSocCodesToLocalValue(responseFormData.occupations, options);
		setLocalValue(selected);
	}, [responseFormData, options]);

	const updateSocQuantity = () => {
		setFormData({ ...formData, soc_occupation_quantities: socQuantity });
		setOpenModal(false);
	};

	useEffect(() => {
		if (openModal) {
			setOriginalSocQuantity(JSON.parse(JSON.stringify(socQuantity)));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openModal]);

	const handleCancel = () => {
		setSocQuantity(originalSocQuantity);

		setQuantityValidationError("");

		setOpenModal(false);
	};

	return (
		<div>
			<CleanModal
				title={"SOC Code Quantities"}
				className={"md occupation-select"}
				trigger={openModal}
				setTrigger={setOpenModal}
				onClose={handleCancel}
				customButton={[
					<div className='text-center' key='save-div'>
						<small className='d-block' style={{ marginBottom: "16px" }}>
							You must click "Apply" on the sidebar to see your changes
						</small>
						<div className='text-center button-wrapper justify-content-center'>
							<button type='button' onClick={handleCancel} className='btn secondary outline'>
								Cancel
							</button>
							<button type='button' key='save' onClick={updateSocQuantity} className='btn primary'>
								Confirm
							</button>
						</div>
					</div>,
				]}
			>
				<div className='col col-12 mx-auto'>
					<p>Adjust the quantity and target wage for each selected SOC code:</p>
					{quantityValidationError && (
						<div className='input-invalid-message' style={{ marginBottom: "16px" }}>
							* {quantityValidationError}
						</div>
					)}
					{options.map(({ label, value }) => {
						const socCode = value;
						const socName = label;

						const currentEntry = socQuantity.find((entry) => entry.soc_code === socCode);
						const currentQuantityValue = currentEntry ? currentEntry.quantity : "";
						const currentTargetWageValue = currentEntry ? currentEntry.target_wage : "";

						return (
							<div className='d-flex' key={socCode}>
								<div>
									<h6>SOC</h6>
									<div>
										{socCode} - {socName}
									</div>
								</div>
								<div className='ml-auto d-flex align-items-end' style={{ gap: "16px" }}>
									<InputText
										wrapperClasses={"step-2-wrapper quantity"}
										labelClass={"lg"}
										key={`quantity_${socCode}`}
										label={"Quantity"}
										placeholder='Enter quantity'
										footnote=''
										value={currentQuantityValue}
										onValueChange={(newValue) => {
											const sanitizedValue = newValue.replace(/\D/g, "").slice(0, 4);

											setSocQuantity((prev) => {
												const existingIndex = prev.findIndex(
													(entry) => entry.soc_code === socCode,
												);
												if (existingIndex !== -1) {
													const updated = [...prev];
													updated[existingIndex] = {
														...updated[existingIndex],
														quantity: sanitizedValue,
													};
													return updated;
												} else {
													return [
														...prev,
														{
															soc_code: socCode,
															occupation: socName,
															quantity: sanitizedValue,
															target_wage: getDefaultTargetWage(socCode),
														},
													];
												}
											});
											if (quantityValidationError) {
												setQuantityValidationError("");
											}
										}}
									/>

									<InputText
										wrapperClasses={"step-2-wrapper target-wage"}
										labelClass={"lg"}
										defaultValue={getDefaultTargetWage(socCode)}
										className={""}
										prefix={"$"}
										label='Target wage'
										placeholder='Enter a target wage for this occupation'
										footnote=''
										key={`target_wage_${socCode}`}
										required
										value={currentTargetWageValue || getDefaultTargetWage(socCode)}
										onValueChange={(value) => {
											const finalValue = value || getDefaultTargetWage(socCode);

											setSocQuantity((prev) => {
												const existingIndex = prev.findIndex(
													(entry) => entry.soc_code === socCode,
												);
												if (existingIndex !== -1) {
													const updated = [...prev];
													updated[existingIndex] = {
														...updated[existingIndex],
														target_wage: finalValue,
													};
													return updated;
												} else {
													return [
														...prev,
														{
															soc_code: socCode,
															occupation: socName,
															quantity: "1",
															target_wage: finalValue,
														},
													];
												}
											});
											if (quantityValidationError) {
												setQuantityValidationError("");
											}
										}}
									/>
								</div>
							</div>
						);
					})}
				</div>
			</CleanModal>

			<div style={{ display: "flex", width: "250px", maxWidth: "250px" }} className='align-items-end w-100'>
				<InputMultiSelect
					className={"mb-0"}
					includesIcon={includesIcon}
					label={"Occupations"}
					labelledBy={"Select occupations"}
					options={options}
					overrideStrings={{
						allItemsAreSelected: "All occupations",
						selectAll: "All occupations",
						selectSomeItems: "Select an occupation",
					}}
					value={localValue}
					onValueChange={(occupations) => {
						onValueChange(occupations.map((item) => item.value));
						setLocalValue(occupations);

						const selected = options.filter((option) => occupations.indexOf(option) > 0);
						trackEvent("Click", {
							Location: "Filter Menu",
							Dashboard: "Score Insights",
							"Sub-Dashboard": currentDashboard?.name,
							Item: "Occupation Sites",
							SOC: selected.map((item) => item.value).join(","),
							Action: "Toggle On/Off",
						});
					}}
				/>
				<div onClick={() => setOpenModal(true)}>
					<Icon className={"pointer"} icon={"gear-line"} />
				</div>
			</div>
		</div>
	);
}

export default OccupationsSelect;
